package com.essntl.uikit.components.dropdown

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.icons.EsSmallIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState

@Composable
fun <T> EsSearchableDropDown(
    itemList: List<T>,
    isLoading: Boolean,
    findItem: (T, String) -> Boolean,
    itemName: String,
    emptyListText: String,
    placeholder: String,
    message: String? = null,
    label: String = "",
    state: EsInputState = EsInputState.Default,
    enabled: Boolean = true,
    content: @Composable ColumnScope.(FocusManager, MutableState<Boolean>, List<T>) -> Unit,
    modifier: Modifier = Modifier,
) {
    var searchText by remember {
        mutableStateOf("")
    }

    val filteredList =
        remember(itemList, searchText) {
            itemList.filter {
                findItem(it, searchText)
            }
        }

    val focusManager = LocalFocusManager.current

    val isDropDownExpanded = remember { mutableStateOf(false) }

    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsFocusedAsState()

    LaunchedEffect(isPressed) {
        if (isPressed && enabled) {
            isDropDownExpanded.value = isPressed
        }
    }

    Column(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        EsInput(
            value = itemName,
            placeholder = placeholder,
            onValueChange = {},
            enabled = enabled,
            label = label,
            message = message,
            state = state,
            suffix = {
                if (isDropDownExpanded.value) {
                    EsSmallIcon(
                        res = EsIcons.ArrowUp,
                    )
                } else {
                    EsSmallIcon(
                        res = EsIcons.ArrowDown,
                    )
                }
            },
            readOnly = true,
            interactionSource = interactionSource,
        )

        if (isDropDownExpanded.value) Spacer(modifier = Modifier.height(0.spacing))
        EsDropDownMenu(
            expanded = isDropDownExpanded.value,
            onDismissRequest = {
                isDropDownExpanded.value = false
                focusManager.clearFocus()
            },
            modifier = modifier,
        ) {
            Spacer(Modifier.height(0.5.spacing))
            EsInput(
                value = searchText,
                onValueChange = {
                    searchText = it
                },
                placeholder = "Search",
                enabled = itemList.isNotEmpty(),
                prefix = {
                    EsMediumIcon(res = EsIcons.Search)
                },
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(Modifier.height(1.spacing))

            if (isLoading) {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier =
                        Modifier
                            .height(40.dp),
                ) {
                    EsSpinner(
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            } else if (filteredList.isNotEmpty()) {
                content(focusManager, isDropDownExpanded, filteredList)
            } else {
                Box(
                    modifier =
                        Modifier
                            .height(40.dp)
                            .padding(1.spacing),
                ) {
                    Text(
                        text = emptyListText,
                        style = MaterialTheme.esTypography.p2Regular14,
                    )
                }
            }
        }
    }
}
